import React from 'react';
import { isEmpty } from '~/util/Validation/String';
import { isNil } from 'ramda';
import InputLabel from '~/components/atom/InputLabel';
import JustificationContainer from '~/components/atom/JustificationContainer';
import styled, { css } from 'styled-components';

type Ref = HTMLTextAreaElement;

type Props = {
  name: string;
  value: string | null;
  onChange: (value: string | null, name: string) => void;
  /** Error, this replaces the label if its set. */
  error?: string | null | undefined;

  /** Disable the input */
  disabled?: boolean;

  /** If the border should be shown or not */
  borderless?: boolean;

  /** testId for test */

  /** Makes the text area uneditable */
  uneditable?: boolean;

  /** Makes the text area resizable */
  resizable?: boolean;
  label?: React.ReactNode;

  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  required?: boolean;
  height?: string;
};
const Textarea = React.forwardRef<Ref, Props>(
  (
    {
      disabled,
      error,
      value,
      dataTestId,
      borderless,
      uneditable,
      label,
      onChange,
      name,
      autoFocus = false,
      resizable = false,
      required,
      height,
      ...rest
    },
    ref,
  ) => (
    <JustificationContainer width="100%" height={height} direction="column">
      {!isNil(label) && !isEmpty(label) && (
        <InputLabel
          required={required}
          error={error}
          label={label}
          size="medium"
        />
      )}
      <TextareaElement
        ref={ref}
        disabled={disabled}
        data-testid={dataTestId || 'test-textarea'}
        value={value || ''}
        $error={error}
        data-error={error}
        rows={4}
        $borderless={borderless}
        $uneditable={uneditable}
        $resizable={resizable}
        autoFocus={autoFocus}
        onChange={e => {
          if (e.target != null) {
            const { value: newValue } = e.currentTarget;

            if (newValue == null || newValue == '') {
              if (value !== null) {
                onChange(null, name);
              }
            } else {
              onChange(newValue, name);
            }
          }
        }}
        {...rest}
      />
    </JustificationContainer>
  ),
);

export const TEXTAREA_ELEMENT_BORDER = '1px';
type StyledTextareaProps = {
  $disabled?: boolean;
  $error?: string | null;
  $borderless?: Props['borderless'];
  $uneditable?: Props['uneditable'];
  $resizable?: Props['resizable'];
} & React.HtmlHTMLAttributes<HTMLTextAreaElement>;

const TextareaElement = styled.textarea<StyledTextareaProps>(
  ({ theme, $resizable, $disabled, $borderless, $uneditable, $error }) => css`
    resize: ${$resizable ? 'both' : 'none'};
    width: 100%;
    box-sizing: border-box;
    border-radius: ${theme.getTokens().border.radius.base};
    border-width: ${theme.getTokens().border.width.s};
    border-style: solid;
    border-color: ${theme.color('tertiary')};

    padding: ${theme.space('s')};

    ${$disabled &&
    css`
      background: ${theme.color('white', 'dark')};
      color: ${theme.color('tertiary', 'dark')};
      pointer-events: none;
    `}

    ${$uneditable &&
    css`
      cursor: default;
      user-select: none;
    `}

    &:hover,
    &:focus {
      ${$uneditable &&
      css`
        border-color: ${theme.color('tertiary')};
      `}

      ${!$uneditable &&
      !$disabled &&
      !$borderless &&
      css`
        border-color: ${theme.color('primary', 'light')};
      `}
    }

    ${!isNil($error) &&
    css`
      &,
      &:hover,
      &:focus {
        border-color: ${theme.color('danger')};
      }
    `}

    ${$borderless &&
    css`
      border: 0;
    `}
  `,
);

export default Textarea;
